import { createFileRoute } from "@tanstack/react-router";
import { Check, Phone } from "lucide-react";
import { site } from "@/data/site";
import { Section, SectionHeading } from "@/components/site/Section";
import { QuoteForm } from "@/components/site/QuoteForm";
import { ReviewPlatforms } from "@/components/site/ReviewsBlock";
import { ReviewBar } from "@/components/site/ReviewBar";
import { MediaGallery } from "@/components/site/MediaGallery";
import { meta } from "@/lib/schema";
import of7 from "@/assets/of-img_0407.jpg.asset.json";
import of8 from "@/assets/of-img_0408.jpg.asset.json";
import of10 from "@/assets/of-img_0410.jpg.asset.json";
import of11 from "@/assets/of-img_0411.jpg.asset.json";
import of12 from "@/assets/of-img_0412.jpg.asset.json";
import of13 from "@/assets/of-img_0413.jpg.asset.json";
import { overviewKeywords } from "@/lib/localKeywords";

const title = "Verhuisofferte aanvragen: gratis en vrijblijvend | Vakkundig";
const description =
  "Vraag een gratis verhuisofferte aan bij een verhuisbedrijf met 4,9/5 uit 507 reviews. Vul je adressen, verdiepingen en inboedel in en ontvang binnen één werkdag een duidelijke prijs zonder verrassingen.";

const steps = [
  "Je vult het formulier in met je verhuisgegevens",
  "Wij nemen contact op en plannen zo nodig een gratis opname",
  "Je ontvangt een duidelijke offerte op maat",
  "Na jouw akkoord zetten we de verhuisdatum vast",
];

const gallery = [
  {
    type: "image" as const,
    service: "particuliere-verhuizing",
    src: of8.url,
    alt: "Twee bakwagens van Vakkundig Verhuizingen & Transport in een woonwijk",
    caption: "Eigen wagenpark, altijd voldoende laadruimte",
  },
  {
    type: "image" as const,
    service: "particuliere-verhuizing",
    src: of7.url,
    alt: "Verhuizer laadt verhuisdozen in de bakwagen via de verhuislift",
    caption: "Inladen via de verhuislift — snel en zonder trappen",
  },
  {
    type: "image" as const,
    service: "particuliere-verhuizing",
    src: of10.url,
    alt: "Meubels ingepakt in wikkelfolie en verhuisdekens",
    caption: "Grote meubels standaard beschermd met folie en dekens",
  },
  {
    type: "image" as const,
    service: "particuliere-verhuizing",
    src: of12.url,
    alt: "Bakwagen van Vakkundig voor een herenhuis in Den Haag",
    caption: "Ook in smalle binnenstadstraten netjes geregeld",
  },
  {
    type: "image" as const,
    service: "particuliere-verhuizing",
    src: of11.url,
    alt: "Bakwagen bij een nieuwbouwwoning met balkon",
    caption: "Nieuwbouw, appartement of eengezinswoning",
  },
  {
    type: "image" as const,
    service: "particuliere-verhuizing",
    src: of13.url,
    alt: "Twee verhuiswagens van Vakkundig achter een bedrijfspand",
    caption: "Particulier, zakelijk en ontruimingen",
  },
];

export const Route = createFileRoute("/offerte")({
  head: () => meta({
      title,
      description,
      path: "/offerte",
      image: "dozen",
      keywords: overviewKeywords(["verhuisbedrijf offerte aanvragen", "verhuiskosten berekenen", "gratis verhuisofferte"]),
    }),
  component: QuotePage,
});

function QuotePage() {
  return (
    <>
      <Section tone="navy">
        <SectionHeading
          as="h1"
          eyebrow="Offerte aanvragen"
          title="Ontvang een gratis offerte op maat"
          inverted
        />
      </Section>

      <ReviewBar />

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div className="rounded-2xl border border-border bg-background p-6 shadow-[var(--shadow-card)] sm:p-8">
            <QuoteForm />
          </div>
          <aside>
            <img
              src={of8.url}
              alt="Bakwagens van Vakkundig Verhuizingen & Transport klaar voor een verhuizing"
              loading="lazy"
              className="mb-6 h-56 w-full rounded-2xl object-cover shadow-[var(--shadow-card)]"
            />
            <div className="rounded-2xl border border-border bg-muted p-6">
              <h2 className="font-display text-xl font-bold text-secondary">Zo werkt het</h2>
              <ol className="mt-4 space-y-3">
                {steps.map((step) => (
                  <li key={step} className="flex gap-3 text-sm text-secondary">
                    <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
                    {step}
                  </li>
                ))}
              </ol>
              <a
                href={site.phoneHref}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-bold text-brand-foreground transition hover:bg-brand-dark"
              >
                <Phone className="size-4" aria-hidden />
                Liever bellen? {site.phone}
              </a>
            </div>
            <img
              src={of10.url}
              alt="Meubels ingepakt in wikkelfolie en verhuisdekens"
              loading="lazy"
              className="mt-6 h-56 w-full rounded-2xl object-cover shadow-[var(--shadow-card)]"
            />
            <div className="mt-6">
              <ReviewPlatforms />
            </div>
          </aside>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Onze verhuizingen in beeld"
          title="Dit krijg je voor jouw offerte"
          intro="Eigen bakwagens, ervaren verhuizers en professioneel verhuismateriaal. Zo pakken wij een verhuizing van begin tot eind aan."
        />
        <div className="mt-8">
          <MediaGallery items={gallery} />
        </div>
      </Section>
    </>
  );
}
